import { Footprints, TramFront, ArrowUpRight } from "lucide-react";
import { useLang, ADDRESS } from "../i18n";
import { Reveal, Overline } from "./Reveal";
import { useLightbox } from "./Lightbox";

const DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=Via+Giovanni+Vitelleschi+6,+00193+Roma,+Italy";

const Surroundings = () => {
  const { t } = useLang();
  const openLightbox = useLightbox();
  return (
    <section id="dintorni" data-testid="surroundings-section" className="py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <Overline>{t.surroundings.overline}</Overline>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl tracking-tight mt-4 max-w-2xl">
            {t.surroundings.title}
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#14213D]/70 leading-relaxed max-w-xl">
            {t.surroundings.desc}
          </p>
        </Reveal>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.surroundings.attractions.map((a, i) => (
            <Reveal key={a.name} delay={i * 0.08}>
              <div
                data-testid={`attraction-card-${i}`}
                className="group rounded-[1.5rem] overflow-hidden border border-[#14213D]/10 bg-white/60 hover:border-[#D39A2E]/50 transition-colors duration-300 h-full"
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={a.img}
                    alt={a.name}
                    loading="lazy"
                    onClick={() => openLightbox(a.img, a.name)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out cursor-zoom-in"
                  />
                  <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-[#F9F8F6]/90 backdrop-blur px-3.5 py-1.5 text-xs tracking-wide text-[#14213D]">
                    <Footprints size={13} className="text-[#D39A2E]" />
                    {a.time} {t.surroundings.walkBadge}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl">{a.name}</h3>
                  <p className="mt-2 text-sm text-[#14213D]/60 leading-relaxed">{a.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 lg:mt-24 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div>
              {t.surroundings.items.map((item, i) => (
                <Reveal key={item.place} delay={i * 0.05}>
                  <div
                    data-testid={`distance-row-${i}`}
                    className="flex items-center justify-between border-b border-[#14213D]/10 py-5 group"
                  >
                    <span className="font-serif text-xl md:text-2xl group-hover:text-[#2852A6] transition-colors duration-300">
                      {item.place}
                    </span>
                    <span className="flex items-center gap-2 text-sm text-[#14213D]/60">
                      <Footprints size={15} className="text-[#D39A2E]" />
                      {item.time}
                    </span>
                  </div>
                </Reveal>
              ))}
              <p className="mt-5 text-xs uppercase tracking-[0.15em] text-[#14213D]/40">{t.surroundings.note}</p>
              <Reveal delay={0.1}>
                <div data-testid="transport-info" className="mt-8 flex items-start gap-4">
                  <TramFront size={18} className="text-[#2852A6] mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#14213D]/50">{t.surroundings.transportLabel}</p>
                    <p className="font-serif text-lg mt-1">{t.surroundings.transport}</p>
                  </div>
                </div>
                <a
                  data-testid="directions-cta"
                  href={DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#2852A6] text-[#F9F8F6] px-7 py-3.5 text-sm tracking-wide hover:bg-[#14213D] transition-colors duration-300"
                >
                  {t.surroundings.directions}
                  <ArrowUpRight size={16} />
                </a>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="relative h-full min-h-[420px]">
              <div className="overflow-hidden rounded-[1.5rem] h-full">
                <iframe
                  title={`King's Place — ${ADDRESS}`}
                  data-testid="map-iframe"
                  src="https://www.google.com/maps?q=Via%20Giovanni%20Vitelleschi%206%2C%2000193%20Roma%2C%20Italy&z=16&output=embed"
                  className="w-full h-full min-h-[420px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="absolute bottom-6 left-6 bg-[#F9F8F6]/90 backdrop-blur-xl rounded-2xl px-6 py-4 shadow-lg">
                <p className="text-xs uppercase tracking-[0.2em] text-[#2852A6]">King's Place</p>
                <p className="font-serif text-lg mt-1">{ADDRESS}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Surroundings;
